
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";

import { trackEvent } from "../../analytics/gtag";
import { EVENTS } from "../../analytics/events";
import {
  fetchAllCartsAsync,
  selectAllCarts,
  removeFromCartsAsync,
} from "../../features/cartPage/cartPageSlice";
import { selectUser } from "../../features/auth/authSlice";
import { calculateDealerPrice, formatWithCommasWithDecimal } from "../../utils/utils";
import { X } from "lucide-react";

// Capitalize first letter helper
const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

// ----- MiniCartItem Component -----
const MiniCartItem = ({ item, onRemove }) => {
  const dealerPrice = formatWithCommasWithDecimal(
    calculateDealerPrice(
      parseInt(item?.skuDetails?.MRP_price),
      parseInt(item?.skuDetails?.NDP_Price),
      parseInt(item?.skuDetails?.GST)
    )
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="w-full flex items-center relative bg-gray-50 mb-2 rounded-md p-2 hover:shadow-lg transition-shadow duration-300"
    >
      <X
        className="absolute right-2 top-2 cursor-pointer text-gray-600 hover:text-red-500"
        size={16}
        onClick={() => onRemove(item)}
      />
      <img
        loading="lazy"
        className="w-[30%] h-full rounded-md object-contain bg-white-1 p-1"
        src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${item?.images}`}
        alt={item?.skuDetails?.prod_name || "Product"}
      />
      <div className="w-[70%] flex flex-col ml-2 justify-between h-full">
        {item?.skuDetails?.manufacture_images ? (
          <img
            loading="lazy"
            className="w-auto h-4 object-contain mb-1"
            src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_MANUFACTURE_IMAGE/${item?.skuDetails?.manufacture_images}`}
            alt="Manufacturer"
          />
        ) : (
          <h3 className="text-xs font-semibold text-gray-700">
            {item?.skuDetails?.manufacture_name}
          </h3>
        )}
        <span className="text-sm font-bold text-gray-800 truncate">
          {item?.skuDetails?.prod_name?.slice(0, 40)}
        </span>
        <div className="flex justify-between items-center text-sm text-gray-600 mt-1">
          <span>
            Qty: <b>{item?.buy_quantity}</b>
          </span>
          <span>₹{dealerPrice}</span>
        </div>
      </div>
    </motion.div>
  );
};

// ----- MiniCart Component -----
const MiniCart = ({isOpen,onClose}) => {
  const userDetail = useSelector(selectUser);
  const carts = useSelector(selectAllCarts);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartRef = useRef(null);

  useEffect(() => {
    if (userDetail?.UserID) {
      dispatch(fetchAllCartsAsync(userDetail.UserID));
    }
  }, [dispatch, userDetail]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleCartNavigate = () => {
    navigate("/carts");
    trackEvent({
      action: EVENTS.CART_STEP,
      category: "Cart Step",
      label: `User Enter Cart Step`,
      value: userDetail?.PhoneNumber,
    });
  };

  const handleRemoveFromCarts = (item) => {
    dispatch(removeFromCartsAsync({ userID: userDetail?.UserID, SKU: item?.skuDetails?.SKU_ID }))
      .then(() => dispatch(fetchAllCartsAsync(userDetail?.UserID)));

    trackEvent({
      action: EVENTS.REMOVE_FROM_CART,
      category: "Cart",
      label: `User Remove Product-> SKU: ${item?.skuDetails?.SKU_ID}`,
      value: userDetail?.PhoneNumber,
    });
  };

  if (!carts || carts.length === 0) return null;

  return (
    <div ref={cartRef} className="absolute top-full right-0 w-64 bg-white-1 rounded-lg shadow-lg p-3 z-50">
      <AnimatePresence>
        {carts.slice(0, 2).map((item, idx) => (
          <MiniCartItem key={idx} item={item} onRemove={handleRemoveFromCarts} />
        ))}
      </AnimatePresence>
      <button
        onClick={handleCartNavigate}
        className="w-full mt-2 py-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white-1 font-semibold rounded-md hover:scale-105 transition-transform duration-200"
      >
        View Cart
      </button>
    </div>
  );
};

export default MiniCart;
